import { Spacing, Typography } from '@persona-id/design-tokens';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FormEvent, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Paragraph } from 'design-system/components/Text';
import inputMixin from 'design-system/mixins/input';
import useToggle from 'lib/hooks/useToggle';
import { captureMessage } from 'lib/reporting';

interface FormBillingInfoProps {
  handleSubmit: (stripeId: string) => Promise<void>;
  handleClose: () => void;
  showMessage: (message: string) => void;
}

// NOTE: Because this is the only component in our client that uses Stripe Elements,
// it is wrapped here instead of at the Application level.
const FormBillingInfo = (props: FormBillingInfoProps) => {
  const stripePromise = useMemo(() => {
    return loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY!).catch((error) => {
      captureMessage(error, 'warning');
      return null;
    });
  }, []);

  return (
    <Elements stripe={stripePromise}>
      <FormBillingInfoContents {...props} />
    </Elements>
  );
};

const FormBillingInfoContents = (props: FormBillingInfoProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const [active, { enable: onInputFocus, disable: onInputBlur }] = useToggle();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (ev: FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      setIsSubmitting(true);

      if (window.Cypress) {
        props.handleSubmit('id');
      }

      const cardElement = elements?.getElement(CardElement);

      if (!stripe || !cardElement) {
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        // Show Stripe error. Stripe's error messages are meant to be user facing.
        props.showMessage(error.message || 'An error occurred. Please try again.');
      } else if (paymentMethod) {
        await props.handleSubmit(paymentMethod.id);
      }

      setIsSubmitting(false);
    },
    [stripe, elements, props],
  );

  return (
    <form onSubmit={handleSubmit}>
      <Heading size='sm'>Payment details</Heading>
      <Spacer height={2} />
      <CardContainer active={active}>
        <CardElement
          onBlur={onInputBlur}
          onFocus={onInputFocus}
          options={{
            style: {
              base: {
                fontSize: Typography.TextMedium.fontSize,
                fontFamily: 'Arial',
              },
            },
          }}
        />
      </CardContainer>
      <HStack equal={true}>
        <Button
          id='form-billing-info__cancel'
          data-test='form-billing-info__cancel'
          onClick={props.handleClose}
          variant='basic'
        >
          Cancel
        </Button>
        <Button
          type='submit'
          id='form-billing-info__save'
          data-test='form-billing-info__save'
          disabled={isSubmitting}
          isPending={isSubmitting}
        >
          Use this card
        </Button>
      </HStack>
      <Spacer height={3} />
      <Paragraph size='sm'>
        By providing your payment information, you agree and acknowledge that Persona will
        automatically bill your payment instrument when you exceed the free allotment as set forth.
      </Paragraph>
    </form>
  );
};

const CardContainer = styled('div')<{ active: boolean }>`
  ${inputMixin};
  margin-bottom: ${Spacing[4]};
`;

export default FormBillingInfo;
